import PropTypes from 'prop-types';
import React, { useEffect, useRef, useCallback } from 'react';
import { filter } from 'lodash';
import { useLocation } from 'react-router-dom';
import { TrendChart } from '../../../CommonCharts/Trend';
import {
    EnableUniqueScaling,
    LastSyncDate,
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup,
    DownloadAll
} from 'sharedComponents/Chart/headers';
import { getFlatAvailableKPIList } from '../../../../commonTrendHelpers';
import './style.scss';
import { LimitSelector } from '../LimitSelector';
import { getSelectedLimitValues } from '../LimitSelector/helper';
import ViewToggleHeader from '../EnergyRelatedSignalsList/ViewToggle/ViewToggleHeader';
import { energyViewModes, STACKED_AREA_CHART } from '../EnergyRelatedSignalsList/constants';
import useUserCapability from 'sharedHooks/useUserCapability';

const TrendChartComponent = ({
    asset: { isMonitored },
    model: {
        selectedTrendKPIs,
        userLimits,
        enableUniqueScaling,
        lastMeasurementTimeStampLabel,
        availableTrendKPIs,
        selectedTrendsData: {
            data,
            loadingStatus,
            zoom: { isZoomed, from: zoomFrom, to: zoomTo },
            chartID
        },
        dateSelection,
        selectedAsset,
        selectedLimits,
        maintainSelectedLimits,
        trendKPIsList,
        selectedOrganization,
        trendComments,
        energyViewMode,
        isOnlyEnergySignalsSelected,
        hasMixedSignalSelection,
        selectedDataPointTimestamp
    },
    actions: {
        reloadAllSelectedTrendKpiData,
        setIsZoomed,
        zoomInToSelectedTrendKpiData,
        removeSelectedKpiLegend,
        clearSelectedKpi,
        resetSelectedKpi,
        setEnergyViewMode,
        setTrendCommentsDrawerVisible,
        setTrendCommentsModalVisible,
        getTrendCommentsRequest
    } }) => {

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const assetId = searchParams.get('assetId');

    const prevAssetIdRef = useRef(selectedAsset);
    const isInitialRender = useRef(true);
    // trigger a cleanup reload to Remove sub-signals from kpiList.
    const prevEnergyViewModeRef = useRef(energyViewMode);

    const userCapabilityHelper = useUserCapability();
    const hasEnergyCapability = userCapabilityHelper.hasOperationalParametersEnergyCapability();

    const selectedSignal = trendKPIsList?.length && trendKPIsList?.find((lmt) => lmt?.timeseriesKey === selectedTrendKPIs[0]);
    const selectedLimitValues = getSelectedLimitValues(maintainSelectedLimits, selectedAsset, selectedSignal, selectedOrganization, selectedLimits);

    const optimizedResetSelectedKpi = useCallback(() => {
        resetSelectedKpi();
    }, [resetSelectedKpi]);

    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            prevAssetIdRef.current = selectedAsset;
            return;
        }

        if (prevAssetIdRef.current?.componentType?.typeName !== selectedAsset?.componentType?.typeName) {
            optimizedResetSelectedKpi();
            prevAssetIdRef.current = selectedAsset;
        }
    }, [assetId, optimizedResetSelectedKpi, selectedAsset]);

    // Filter selected trends data. Sub-signals are only included in Detailed view mode;
    const filteredSelectedTrendsData = filter(data, (item) => {
        // Include if directly selected
        if (selectedTrendKPIs?.includes(item?.kpiIdentifier)) {
            return true;
        }

        // Sub-signals are only relevant in Detailed energy view
        if (energyViewMode !== energyViewModes.DETAILED) {
            return false;
        }

        // Check if this signal is a sub-signal of any selected stacked area chart
        const parentSignal = data?.find(d =>
            selectedTrendKPIs?.includes(d?.kpiIdentifier) &&
            d?.config?.details?.visualization === STACKED_AREA_CHART
        );

        if (parentSignal?.config?.details?.subSignals) {
            // Check if current item matches any sub-signal by timeseriesKey
            return parentSignal?.config?.details?.subSignals?.some(
                subSig => subSig?.timeseriesKey === item?.kpiIdentifier
            );
        }

        return false;
    });

    // Check if there's actual chart data available for the selected energy signals
    const hasChartData = filteredSelectedTrendsData && filteredSelectedTrendsData?.length > 0 &&
        filteredSelectedTrendsData?.some(item => item?.values && item?.values?.length > 0);

    // Check if selected signal has StackedAreaChart visualization
    // Only enable stacked area chart / Overall+Detailed toggle when energy capability is granted
    const hasStackedAreaChartVisualization = hasEnergyCapability &&
        selectedTrendKPIs?.length === 1 &&
        filteredSelectedTrendsData?.some(item =>
            item?.config?.details?.visualization === STACKED_AREA_CHART &&
            item?.config?.details?.subSignals?.length >= 2
        );

    const isDetailedEnergyView = energyViewMode === energyViewModes.DETAILED &&
        isOnlyEnergySignalsSelected &&
        hasStackedAreaChartVisualization;
    const zoomInServer = (availableTrendKPIs, selectedTrendKPIs, from, to) => {
        const trendKPIList = getFlatAvailableKPIList(availableTrendKPIs);
        const filteredArray = selectedTrendKPIs?.map((kpi) => {
            return trendKPIList[kpi];
        });
        zoomInToSelectedTrendKpiData(filteredArray, from, to);
    };

    const resetServerZoom = () => {
        reloadAllSelectedTrendKpiData(selectedTrendKPIs);
    };

    useEffect(() => {
        if (!isMonitored) {
            clearSelectedKpi();
        }
    }, [isMonitored]);

    // Reset energy view mode to OVERALL when stacked area chart conditions are no longer met
    useEffect(() => {
        if (!hasStackedAreaChartVisualization && energyViewMode === energyViewModes.DETAILED) {
            setEnergyViewMode(energyViewModes.OVERALL);
        }
    }, [hasStackedAreaChartVisualization]);

    useEffect(() => {
        const prevMode = prevEnergyViewModeRef?.current;
        prevEnergyViewModeRef.current = energyViewMode;

        if (energyViewMode === energyViewModes.DETAILED && hasStackedAreaChartVisualization && selectedTrendKPIs?.length) {
            if (isZoomed && zoomFrom && zoomTo) {
                zoomInServer(availableTrendKPIs, selectedTrendKPIs, zoomFrom, zoomTo);
            } else {
                reloadAllSelectedTrendKpiData(selectedTrendKPIs);
            }
        } else if (energyViewMode === energyViewModes.OVERALL && prevMode === energyViewModes.DETAILED && selectedTrendKPIs?.length) {
            reloadAllSelectedTrendKpiData(selectedTrendKPIs);
        }
    }, [energyViewMode, hasStackedAreaChartVisualization]);

    // ViewToggle component for energy signals
    const ViewToggle = props =>
        <ViewToggleHeader
            currentViewMode={energyViewMode}
            onViewModeChange={setEnergyViewMode}
            isOnlyEnergySignalsSelected={isOnlyEnergySignalsSelected}
            hasMixedSignalSelection={hasMixedSignalSelection}
            selectedSignalsData={filteredSelectedTrendsData}
            {...props}
        />;

    // Build right button group conditionally - hide LimitSelector for energy-only signals
    // Only show ViewToggle if signal has StackedAreaChart visualization with chart data
    const rightButtons = hasStackedAreaChartVisualization && hasChartData
        ? [LimitSelector, ViewToggle, ResetZoomButton, DownloadAll, PopOutButton]
        : [LimitSelector, ResetZoomButton, DownloadAll, PopOutButton];
    // Conditionally build the left button group based on view mode
    const leftButtonGroup = isDetailedEnergyView
        ? withLeftButtonGroup([LastSyncDate])
        : withLeftButtonGroup([LastSyncDate, EnableUniqueScaling]);

    const CustomHeader = withPopOutRow([
        leftButtonGroup,
        withRightButtonGroup(rightButtons)]);

    return (
        <TrendChart
            selectedTrendKPIs={selectedTrendKPIs}
            selectedTrendsData={filteredSelectedTrendsData}
            removeSelectedKpiLegend={removeSelectedKpiLegend}
            trendDataLoadingStatus={loadingStatus}
            userLimits={userLimits}
            enableUniqueScaling={enableUniqueScaling}
            lastMeasurementTimeStampLabel={lastMeasurementTimeStampLabel}
            isZoomed={isZoomed}
            availableTrendKPIs={availableTrendKPIs}
            chartKey={chartID}
            reloadAllSelectedTrendKpiData={resetServerZoom}
            zoomInToSelectedTrendKpiData={zoomInServer}
            setIsZoomed={setIsZoomed}
            mainChartHeader={CustomHeader}
            dateSelection={dateSelection}
            selectedLimits={selectedLimitValues}
            trendComments={trendComments}
            setShowCommentDrawer={setTrendCommentsDrawerVisible}
            setShowCommentModal={setTrendCommentsModalVisible}
            getTrendCommentsRequest={getTrendCommentsRequest}
            energyViewMode={energyViewMode}
            isOnlyEnergySignalsSelected={isOnlyEnergySignalsSelected}
            hasStackedAreaChartVisualization={hasStackedAreaChartVisualization}
            selectedDataPointTimestamp={selectedDataPointTimestamp}
        />
    );
};

TrendChartComponent.propTypes = {
    asset: PropTypes.shape({
        isMonitored: PropTypes.bool.isRequired,
    }),
    actions: PropTypes.shape({
        reloadAllSelectedTrendKpiData: PropTypes.func,
        setIsZoomed: PropTypes.func,
        zoomInToSelectedTrendKpiData: PropTypes.func,
        resetSelectedKpi: PropTypes.func,
        removeSelectedKpiLegend: PropTypes.func,
        clearSelectedKpi: PropTypes.func,
        setEnergyViewMode: PropTypes.func,
    }),
    model: PropTypes.shape({
        selectedTrendKPIs: PropTypes.array,
        enableUniqueScaling: PropTypes.bool,
        lastMeasurementTimeStampLabel: PropTypes.string,
        selectedTrendsData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            data: PropTypes.array,
            zoom: PropTypes.shape({
                isZoomed: PropTypes.bool,
                from: PropTypes.string,
                to: PropTypes.string,
            }),
            chartID: PropTypes.string
        }),
        userLimits: PropTypes.array,
        availableTrendKPIs: PropTypes.array,
        dateSelection: PropTypes.object,
        selectedAsset: PropTypes.shape({
            componentType: PropTypes.shape({
                typeName: PropTypes.string,
            })
        }),
        selectedLimits: PropTypes.array,
        maintainSelectedLimits: PropTypes.object,
        trendKPIsList: PropTypes.array,
        selectedOrganization: PropTypes.object,
        energyViewMode: PropTypes.string,
        isOnlyEnergySignalsSelected: PropTypes.bool,
        hasMixedSignalSelection: PropTypes.bool,
        trendComments: PropTypes.array,
        setShowCommentDrawer: PropTypes.func,
        setShowCommentModal: PropTypes.func,
        getTrendCommentsRequest: PropTypes.func
    })
};

export default TrendChartComponent;
